import axios from "axios";
import toast from "react-hot-toast";
import {
  isAccessExpiredResponse,
  notifyAccessExpired,
  redirectToAccessExpiredPage,
} from "./lib/accessExpired";

const axiosClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL, // TODO: use env var based on environment
  withCredentials: true, // Always send cookies or credentials in requests
});

// Add a request interceptor to always use the latest token
axiosClient.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem("accessTokenOq");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      delete config.headers.Authorization; // remove header if no token
    }
    return config;
  },
  (error) => Promise.reject(error),
);

function getRateLimitMessage(error: any): string {
  const data = error?.response?.data;
  const retryAfterSeconds = Number(data?.retryAfterSeconds);
  const baseMessage =
    typeof data?.error === "string" && data.error.trim().length > 0
      ? data.error
      : "Too many requests. Please slow down and try again shortly.";

  if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
    const minutes = Math.ceil(retryAfterSeconds / 60);
    if (minutes >= 2) {
      return `${baseMessage} Try again in about ${minutes} minutes.`;
    }
    return `${baseMessage} Try again in about ${retryAfterSeconds} seconds.`;
  }

  return baseMessage;
}

// Add a response interceptor
axiosClient.interceptors.response.use(
  // Handle successful responses
  (response) => response,
  // Handle errors
  async (error) => {
    if (error.response?.status === 401) {
      // Avoid redirect loop if already on login page
      const loginUrl = `${process.env.REACT_APP_SITE_URL}/login`;
      if (window.location.href !== loginUrl) {
        alert("Your session has expired. Please login again.");
        window.location.href = loginUrl;
      }
    }

    if (isAccessExpiredResponse(error)) {
      notifyAccessExpired();
      redirectToAccessExpiredPage();
    }

    if (error.response?.status === 429) {
      toast.error(getRateLimitMessage(error), {
        id: "rate-limit",
        duration: 6000,
      });
    }

    return Promise.reject(error);
  },
);

export default axiosClient;
